import { currentUserQuery } from "@common/api/users"
import { useDeleteWorkPackagePhaseMutation } from "@common/api/work-package-phases"
import { workPackageTasksQuery } from "@common/api/work-packages"
import { ConfirmDialog } from "@common/components/shared/confirm-dialog"
import { hasPermission } from "@common/lib/permissions"
import { handleError } from "@common/utils"
import { useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router"
import useCustomToast from "@/hooks/use-custom-toast"

export const Route = createFileRoute(
  "/_layout/work-orders/$workOrderId/phases/$phaseId/delete",
)({
  beforeLoad: async ({ context: { queryClient }, params, search }) => {
    const me = await queryClient.ensureQueryData(currentUserQuery())
    if (!hasPermission(me, "work_package:update")) {
      throw redirect({
        to: "/work-orders/$workOrderId",
        params: { workOrderId: params.workOrderId },
        search,
      })
    }
  },
  component: DeletePhaseDialog,
})

function DeletePhaseDialog(): React.ReactElement {
  const { workOrderId, phaseId } = Route.useParams()
  const { data: workOrder } = useSuspenseQuery(
    workPackageTasksQuery({ id: workOrderId }),
  )
  const navigate = useNavigate()
  const showToast = useCustomToast()
  const mutation = useDeleteWorkPackagePhaseMutation()
  const phase = workOrder.active_phases.find(({ id }) => id === phaseId)
  const close = () =>
    navigate({
      to: "/work-orders/$workOrderId",
      params: { workOrderId },
      search: true,
    })

  return (
    <ConfirmDialog
      title={`Archive ${phase?.name ?? "this phase"}?`}
      description="Its tasks stay on the work order, without a phase."
      action="Archive"
      tone="default"
      pendingAction="Archiving…"
      pending={mutation.isPending}
      onConfirm={() =>
        mutation.mutate(
          { work_package_id: workOrderId, phase_id: phaseId },
          {
            onSuccess: () => {
              showToast("Archived", "Phase archived.", "success")
              close()
            },
            onError: (error) => handleError(error, showToast),
          },
        )
      }
      onCancel={close}
    />
  )
}
